import { getCurrentShowOddsValue, getCurrentShrkValue, getCurrentUserTierValue, normalizeStatsTierValue, primeStatsCache } from "./FetchStats.js";
import { normalizeProjectedStatKey } from "./selectionState.js";
import reportBoardIssue from "./ReportBoardIssue.js";
import restFetchOptions, { getCurrentRestViewer, recoverInvalidRestNonceResponse } from "./restFetchOptions.js";
import { createBoardRequestId, recordBoardRequestTelemetry } from "./BoardRequestTelemetry.js";

// Production cold starts have occasionally completed between 8-10 seconds.
// Keep the deadline below the 15-second visible-loading guard while allowing a
// healthy-but-busy worker to finish instead of aborting and immediately retrying.
const BOARD_BOOTSTRAP_TIMEOUT_MS = 12000;
const BOARD_BOOTSTRAP_PREFETCH_KEY = "__WW_BOARD_BOOTSTRAP_PREFETCH";
const BOARD_BOOTSTRAP_SIGNATURE_PARAMS = ["viewer", "shrk", "tier", "show_odds", "sport", "stat", "limit"];

function getBoardBootstrapSignature(url) {
  if (typeof window === "undefined") {
    return "";
  }

  try {
    const parsedUrl = new URL(url, window.location.origin);
    return BOARD_BOOTSTRAP_SIGNATURE_PARAMS
      .map((key) => `${key}=${parsedUrl.searchParams.get(key) || ""}`)
      .join("&");
  } catch (error) {
    return "";
  }
}

function takePrefetchedBoardBootstrap(url) {
  if (typeof window === "undefined") {
    return null;
  }

  const prefetch = window[BOARD_BOOTSTRAP_PREFETCH_KEY];
  if (
    !prefetch ||
    prefetch.consumed ||
    !prefetch.url ||
    !prefetch.promise ||
    typeof prefetch.promise.then !== "function" ||
    getBoardBootstrapSignature(prefetch.url) !== getBoardBootstrapSignature(url)
  ) {
    return null;
  }

  prefetch.consumed = true;
  return prefetch;
}

export default async function FetchBoardBootstrap(options = {}) {
  const shrk = (options.shrk ?? getCurrentShrkValue()).toString();
  const userTier = normalizeStatsTierValue(options.userTier ?? getCurrentUserTierValue());
  const showOdds = (options.showOdds ?? getCurrentShowOddsValue()).toString();
  const params = new URLSearchParams();

  params.set("viewer", getCurrentRestViewer());
  params.set("shrk", shrk);
  params.set("tier", userTier);
  if (showOdds === "1") {
    params.set("show_odds", "1");
  }

  if (options.sport) {
    params.set("sport", String(options.sport).toLowerCase());
  }

  if (options.stat) {
    params.set("stat", normalizeProjectedStatKey(String(options.stat)));
  }

  if (Number(options.limit) > 0) {
    params.set("limit", String(Math.floor(Number(options.limit))));
  }

  const generatedUrl = `/wp-json/wagedwar/v1/board-bootstrap?${params.toString()}`;
  const prefetchedRequest = takePrefetchedBoardBootstrap(generatedUrl);
  const url = prefetchedRequest?.url || generatedUrl;
  const timeoutMs = Number.isFinite(Number(options.timeoutMs)) && Number(options.timeoutMs) > 0
    ? Number(options.timeoutMs)
    : BOARD_BOOTSTRAP_TIMEOUT_MS;
  const prefetchedController = prefetchedRequest?.controller;
  const controller = prefetchedController?.signal && typeof prefetchedController.abort === "function"
    ? prefetchedController
    : new AbortController();
  const prefetchStartedAt = Number(prefetchedRequest?.startedAt);
  const prefetchElapsedMs = Number.isFinite(prefetchStartedAt)
    ? Math.max(0, Date.now() - prefetchStartedAt)
    : 0;
  const timeoutId = setTimeout(() => controller.abort(), Math.max(1, timeoutMs - prefetchElapsedMs));
  if (typeof window !== "undefined") {
    window.__WW_LAST_BOARD_BOOTSTRAP_URL = url;
  }
  let response;
  const requestId = createBoardRequestId();

  try {
    const fetchOptions = restFetchOptions({ signal: controller.signal, headers: { "X-WW-Request-Id": requestId } });
    response = prefetchedRequest
      ? await prefetchedRequest.promise
      : await fetch(url, fetchOptions);
    response = await recoverInvalidRestNonceResponse(response, url, fetchOptions);
  } catch (error) {
    if (error.name === "AbortError") {
      recordBoardRequestTelemetry({ requestId, httpStatus: 0, outcome: "timeout", route: "board-bootstrap" });
      throw new Error(`Board bootstrap timed out after ${timeoutMs}ms`);
    }
    recordBoardRequestTelemetry({ requestId, httpStatus: 0, outcome: "network-error", route: "board-bootstrap" });
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    recordBoardRequestTelemetry({ requestId, httpStatus: response.status, outcome: "http-error", route: "board-bootstrap" });
    const responseRequestId = response.headers.get("cf-ray")
      || response.headers.get("x-request-id")
      || response.headers.get("x-amzn-trace-id")
      || "";
    const responseMessage = await response.clone().text()
      .then((body) => body.replace(/\s+/g, " ").trim().slice(0, 240))
      .catch(() => "");
    void reportBoardIssue("board-bootstrap-http-error", {
      sport: options.sport || "",
      stat: options.stat || "",
      shrk,
      url,
      requestedLimit: Number(options.limit) > 0 ? Math.floor(Number(options.limit)) : 0,
      status: response.status,
      timeoutMs,
      message: [responseRequestId ? `request-id:${responseRequestId}` : "", responseMessage].filter(Boolean).join(" "),
    });
    throw new Error(`Board bootstrap failed: HTTP ${response.status}`);
  }

  let payload;
  try {
    payload = await response.json();
    recordBoardRequestTelemetry({ requestId, httpStatus: response.status, outcome: "success", route: "board-bootstrap" });
    if (typeof window !== "undefined") {
      window.__WW_BOARD_STAGE_TIMINGS = window.__WW_BOARD_STAGE_TIMINGS || {};
      window.__WW_BOARD_STAGE_TIMINGS.bootstrapReadyAt = Date.now();
    }
  } catch (error) {
    const responseReadWasAborted = controller.signal.aborted
      || error?.name === "AbortError"
      || /\babort(?:ed|ing)?\b/i.test(String(error?.message || ""));

    if (!responseReadWasAborted) {
      void reportBoardIssue("board-bootstrap-invalid-json", {
        sport: options.sport || "",
        stat: options.stat || "",
        shrk,
        url,
        requestedLimit: Number(options.limit) > 0 ? Math.floor(Number(options.limit)) : 0,
        status: response.status,
        timeoutMs,
        message: error.message,
      });
    }
    throw error;
  }

  const payloadIsObject = payload && typeof payload === "object" && !Array.isArray(payload);
  const athletesAreValid = payloadIsObject && Array.isArray(payload.athletes);
  if (!payloadIsObject || !athletesAreValid) {
    void reportBoardIssue("board-bootstrap-broken-payload", {
      sport: options.sport || payload?.sport || "",
      stat: options.stat || payload?.stat || "",
      shrk,
      url,
      requestedLimit: Number(options.limit) > 0 ? Math.floor(Number(options.limit)) : 0,
      status: response.status,
      timeoutMs,
      responseKind: Array.isArray(payload) ? "array" : typeof payload,
      athletesCount: Array.isArray(payload?.athletes) ? payload.athletes.length : -1,
      message: !payloadIsObject ? "Payload is not an object" : "Payload athletes field is not an array",
    });
  }

  if (payload?.availableStats) {
    primeStatsCache(payload.availableStats, shrk, userTier);
  }

  return payload;
}
